import 'echarts/lib/chart/sankey';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
import { merge } from 'lodash';

import ChartService from './ChartService';

export default class ChartSankeyService extends ChartService {

  getSeriesColors(visualization) {
    const colors = this.defaultChartOptions.color;

    const group = visualization.data.nodes.groupBy('name');
    return group
      .map((gr, index) => ({
        key: gr.key,
        color: gr.items[0].color || colors[index],
        label: gr.key
      }));
  }

  buildChartData(visualization, isEditMode, bndBox) {
    const seriesColors = this.getSeriesColors(visualization);
    const getLabelsForMove = () => {
      const links = visualization.data.links.sortBy(f => f.value).reverse();
      const firstLevelNodeIds = visualization.data.nodes
        .filter((node) => !links.filter((l) => l.target === node.id).length)
        .map((f) => f.id);

      const firstLevelNodeLinks = links
        .filter((link) => firstLevelNodeIds.includes(link.source || ''))
        .filter((link) => !links.filter((f) => f.target === link.target
          && !firstLevelNodeIds.includes(f.source || '')).length);

      const secondLevelNodeLinks = firstLevelNodeLinks
        .filter((link) => links.filter((f) => f.source === link.target).length);

      const secondLevelValues = secondLevelNodeLinks.groupBy(f => f.target)
        .map((f) => ({
          id: f.key,
          value: f.items.sum(g => g.value)
        })).sortBy(f => f.value).reverse();

      const firstLevelSorted = firstLevelNodeLinks.sortBy(f => secondLevelValues.map(g => g.id).indexOf(f.target));
      const firstLevelValues = firstLevelSorted.groupBy(f => f.source)
        .map(f => ({
          id: f.key,
          value: f.items.sum(g => g.value)
        }));

      const delta = (firstLevelValues.sum(f => f.value) - secondLevelValues.sum(f => f.value)) / 2;
      const heightPerPx = firstLevelValues.sum(f => f.value) / 500;
      const fontHeight = 14 * heightPerPx;

      const secondLevelLabels = secondLevelValues
        .map((f, ind) => delta + secondLevelValues.take(ind).sum(g => g.value) + f.value / 2);
      return firstLevelValues.map((f, ind) => {
        const valBefore = firstLevelValues.take(ind).sum(g => g.value);
        const val = valBefore + f.value / 2;
        const overlap = secondLevelLabels.find((g) => val - fontHeight < g && val + fontHeight > g);
        return { id: f.id, position: overlap ? `${(overlap - valBefore + fontHeight) * 100 / f.value}%` : null };
      });
    };
    const formatter = (params) => {
      if (params.dataType === 'edge') {
        return `
          Source: <b>${params.data.source}</b> <br />
          Target: <b>${params.data.target}</b> <br />
          Value: <b>${params.data.value}</b>
        `;
      } else {
        return `<b>${params.data.name}</b>`;
      }
    }

    const labelsForMove = getLabelsForMove().filter((f) => f.position);
    const options = {

      // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35314
      series: [
        {
          data: visualization.data.nodes.map(node => {
            let additionalOptions = {};
            const isDiagnosisNode = visualization.data.links.filter((l) => l.target === node.id).length === 0;
            if (isDiagnosisNode) {
              const labelMove = labelsForMove.find((f) => f.id === node.id);
              additionalOptions = {
                itemStyle: {
                  // color: '#767676',
                },
                label: {
                  // color: '#fff',
                  position: labelMove ? ['110%', labelMove.position] : 'right',
                  // rotate: 90,
                },
              };
            }

            const uncheckedColor = 'rgb(216,216,216)';
            const defaultColor = seriesColors.find((f) => f.key === node.name).color;
            const color = isEditMode && node.unchecked ? uncheckedColor : defaultColor;
            return merge({
              itemStyle: {
                color,
              },
              name: node.id,
              title: node.name,
              localX: node.localX,
              localY: node.localY,
            }, additionalOptions);
          }),

          // draggable: false,

          focusNodeAdjacency: 'allEdges',

          itemStyle: {
            borderWidth: 0,
          },

          label: {
            fontSize: 14,
            fontWeight: 'bold',
            formatter: (params) => params.data.title,
            position: 'left',
          },

          lineStyle: {
            color: '#D6DCE7',
            curveness: 0.4,
            opacity: 0.5,
          },

          links: visualization.data.links
            .map(link => {
              const uncheckedColor = 'rgb(216,216,216)';
              const defaultColor = 'rgb(96,96,96)';
              const color = isEditMode && link.unchecked ? uncheckedColor : defaultColor;

              return {
                ...link,
                lineStyle: { color },
              };
            }),

          nodeWidth: 24 * (visualization.zoom || 1),

          ...(visualization.zoom ? { width: `${bndBox.width * visualization.zoom * .8}px`, height: `${bndBox.height * visualization.zoom * .9}px` } : {}),

          right: '15%',

          tooltip: {
            formatter: params => (visualization.formatter || formatter)(params, visualization.data),
          },

          type: 'sankey',
        },
      ],

    };

    return merge(this.defaultChartOptions, {
      ...options,
      seriesColors
    });

  }

}
